import { Dropdown, MultiselectDropdown, OtpModal, RadioButton, Stepper, TextInput } from '@components/common';
import { OTHERS_SPECIFY, siteRadioTypeList } from '@config/constant';
import { useFormik } from 'formik';
import { ReactElement, useCallback, useEffect, useMemo, useState } from 'react';
import { defaultSiteAddressFormInitialValues, veneerOptions } from '.';
import { AccordianDetailList } from '@components/endUserDetails/components/moreDetailsForm/accordianDetailList';
import { useUpdateSiteLeadMutation } from '@services/hooks/site-leads/useUpdateSiteLeadMutation';
import { useValidation } from '@hooks/validations';
import useStepperStore from '@store/stepperStore';
import useSiteLeadStore from '@store/siteLeadStore';
import { useAvailableProductsListQuery } from '@services/hooks/site-leads/useAvailableProductsListQuery';
import { useUploadFreshSiteImages } from '@services/hooks/site-leads/uploadFreshSiteImages';
import { ToastType, useTosterStore } from '@store/toster';
import { ProductImages } from '../../../components/siteProducts/productImages';
import { Wrapper } from '@components/common/Wrapper/wrapper';
import { useGetSiteLeadQuery } from '@services/hooks/site-leads/useGetSiteLeadQuery';
import { DealersList } from '@components/endUserDetails/components/moreDetailsForm';
import { AvailableProductsList, MicroSiteLeadData, UpdateSiteLeadData } from '@services/hooks/site-leads';
import { useGetRxDb } from '@hooks/getRxdbData';
import { useDealersQuery } from '@services/hooks/enduser-details/useDealerQuery';
import { useSetMainHeader } from '@hooks/useSetMainHeader';
import { isNullOrEmpty } from '@components/delaerVisit/components/addEditDealerVisit';
import { DropdownOptions } from '@components/common/formControl/dropdown';
import { useSiteSummaryListing } from '@services/hooks/site-summary-listing';
import { useImageUpload } from '@hooks/useImageUpload';
import { useDealerListRxDb } from '@services/hooks/enduser-details/rxdbHooks/useDealerListRxDb';
import { useDebounce } from '@hooks/useDebounce';
import { Modal } from '@components/common/modal/modal';
import { QrModal } from '@components/dashboard/components/meeting/qrModal';
import { overlayRemoveFadeAction } from '@components/common/sort';
import { ConfirmationModal } from '@components/conductedMeetings/confirmationModal/confirmationModal';
import { SiteLeadConfigurations } from '@components/siteLeads';
import { useKAESSProductListQuery } from '@services/hooks/site-leads/useKAESSProductList';
import { useAddSiteVisitKaessMutation } from '@services/hooks/site-leads/useAddSiteVisitKaessMutation';
import { keyDownEvent } from '@helper/utils';
import {
	productComplaintDescriptionOptions,
	productComplaintOptions,
} from '@components/siteLeads/components/siteAddress';
import { useSiteVisitActiveUrlQuery } from '@services/hooks/site-leads/useSiteVisitActiveUrlQuery';
import { useComponentLevelAccessCheck } from '../../../../../authGuard/hooks/useComponentLevelAccessCheck';

export const KEASSAddNewSiteAddree = (): ReactElement => {
	useSetMainHeader('Add New Site Visit');
	const { setCurrentStep } = useStepperStore();
	const { SiteAddressSSValidationSchema } = useValidation();
	useDealersQuery();
	useAvailableProductsListQuery();
	const { data: siteLeadData, isFetching: isSiteLeadFetching } = useGetSiteLeadQuery();
	const { result: availableProductsDataList } = useGetRxDb<AvailableProductsList>('products');
	const { mutate: updateSiteLead, isPending: isSiteLeadPending } = useUpdateSiteLeadMutation();
	const { dealerName, ownerDetails, siteVisitId, siteDetails } = useSiteLeadStore();
	const { mutate: createVisit, isPending: isSiteVisitsPending } = useAddSiteVisitKaessMutation();
	const [maxProductComplaintLimit, setMaxProductComplaintLimit] = useState<number>(2);
	const { mutate: uploadFreshSiteImages, isPending: isPutSiteImagePending } = useUploadFreshSiteImages();
	const { setToaster } = useTosterStore();
	const { refetch: refetchSiteSummaryList } = useSiteSummaryListing();
	const isDisabled = (siteLeadData?.site_visit_count ?? 0) > 1;
	const { handleAddImage } = useImageUpload();
	const [dealerSearch, setDealerSearch] = useState<string>('');
	const isKAESiteTypeFields = useComponentLevelAccessCheck('has-site-type-permission');

	const { result: dealerList } = useDealerListRxDb({
		searchParams: [
			{ key: 'dealername_c', value: dealerSearch },
			{ key: 'dealercode_c', value: dealerSearch },
		],
		sortBy: 'dealername_c',
	});
	const [openQrModal, setOpenQrModal] = useState<boolean>(false);
	const [confirmationModal, setConfirmationModal] = useState<boolean>(false);
	const [openOtpModal, setOpenOtpModal] = useState<boolean>(false);
	const {
		data: taskListProducts,
		isFetching: isTaskListProductsFetching,
		error: taskListProductsError,
	} = useKAESSProductListQuery(siteDetails?.membershipno);

	const { data: activeUrlData } = useSiteVisitActiveUrlQuery();
	const activeLink = activeUrlData?.data?.[0];
	const onDropDownSearch = useDebounce((e: React.KeyboardEvent<HTMLInputElement>): void => {
		const element = e.target as HTMLInputElement;
		setDealerSearch(element.value);
	}, 300);
	const listProducts = taskListProducts?.data.reduce(
		(
			acc: Array<{
				id: string;
				product_code: string;
				product_name: string;
				product_demo: boolean;
				product_discussed: boolean;
			}>,
			item
		) => {
			let index = 1;
			while (item[`product${index}_code`]) {
				acc.push({
					id: `${item.id}-${index}`,
					product_code: item[`product${index}_code`],
					product_name: item[`product${index}_name`],
					product_demo: item[`product${index}_discussed`],
					product_discussed: item[`product${index}_demo`],
				});
				index++;
			}
			return acc;
		},
		[]
	);

	const formik = useFormik({
		initialValues: defaultSiteAddressFormInitialValues,
		onSubmit: () => {},
		validationSchema: SiteAddressSSValidationSchema,
		validateOnMount: true,
		validate: (values: typeof defaultSiteAddressFormInitialValues & { hasOpenedActiveLink?: boolean }) => {
			const errors: Record<string, string> = {};

			if (activeLink?.mandatory && !values.hasOpenedActiveLink) {
				errors.hasOpenedActiveLink = 'Please open the required link before submitting.';
			}

			return errors;
		},
	});

	const updateSiteProducts = useCallback((): void => {
		const {
			availableProducts,
			productImages,
			complaintProductsList,
			productComplaintDescription,
			customProductComplaintDescription,
		} = formik.values;

		const siteVisitPayload = {
			product_available_at_site: isNullOrEmpty(availableProducts),
			product1_demoed_code: listProducts?.[0]?.product_code,
			product1_demoed_name: listProducts?.[0]?.product_name,
			product2_demoed_code: listProducts?.[1]?.product_code,
			product2_demoed_name: listProducts?.[1]?.product_name,
			product1_discussed:
				listProducts?.[1] && Object.keys(listProducts?.[1]).length !== 0
					? `${listProducts?.[1]?.product_code}-${listProducts?.[1]?.product_name}`
					: null,
			product2_discussed: null,
			end_user_site_r_app_id_c: siteLeadData?.app_id_c ?? '',
			glue_potential: formik.values.gluePotentialOfSite ?? null,
			product1_demoed_mng_c: isNullOrEmpty(complaintProductsList),
			reason_for_using_competition_c:
				productComplaintDescription?.[0]?.value === OTHERS_SPECIFY
					? customProductComplaintDescription ?? null
					: isNullOrEmpty(productComplaintDescription) ?? null,
			weather_work_given_to_joinery_c: formik.values.isWeatherWorkGivenToJoinery ?? null,
			joinery_name:
				formik.values.isWeatherWorkGivenToJoinery?.toLowerCase() === 'yes' ? formik.values.joineryName ?? null : null,
			joinery_phone_number:
				formik.values.isWeatherWorkGivenToJoinery?.toLowerCase() === 'yes' ? formik.values.joineryNumber ?? null : null,
			joinery_address:
				formik.values.isWeatherWorkGivenToJoinery?.toLowerCase() === 'yes'
					? formik.values.joineryAddress ?? null
					: null,
			weather_work_given_to_factory_c: formik.values.isWorkGivenToFactory ?? null,
			factory_name:
				formik.values.isWorkGivenToFactory?.toLowerCase() === 'yes' ? formik.values.factoryName ?? null : null,
			factory_phone_number:
				formik.values.isWorkGivenToFactory?.toLowerCase() === 'yes' ? formik.values.factoryNumber ?? null : null,
			factory_address:
				formik.values.isWorkGivenToFactory?.toLowerCase() === 'yes' ? formik.values.factoryAddress ?? null : null,
		};

		uploadFreshSiteImages(
			{ files: productImages ?? [], folder: 'sitelead' },
			{
				onSuccess: (uploadedImages) => {
					const visitPayload = {
						...siteVisitPayload,
						...(uploadedImages[0]?.normal_url ? { visit_image1: uploadedImages[0].normal_url } : {}),
						...(uploadedImages[1]?.normal_url ? { visit_image2: uploadedImages[1].normal_url } : {}),
						...(uploadedImages[2]?.normal_url ? { visit_image3: uploadedImages[2].normal_url } : {}),
						...(uploadedImages[3]?.normal_url ? { visit_image4: uploadedImages[3].normal_url } : {}),
						...(uploadedImages[4]?.normal_url ? { visit_image5: uploadedImages[4].normal_url } : {}),
					};
					createVisit(visitPayload as MicroSiteLeadData, {
						onSuccess: () => {
							refetchSiteSummaryList?.();
							if (formik?.values?.isContractorAvailable?.toLowerCase() === 'no' && SiteLeadConfigurations.OTP_FLOW) {
								setOpenOtpModal(true);
								return;
							}
							if (SiteLeadConfigurations.QR_FLOW) {
								setOpenQrModal(true);
								return;
							}
							setCurrentStep(2);
						},
						onError: (error) => {
							setToaster(true, ToastType.error, error?.message ?? 'Failed to create site visit.', 5000);
						},
					});
				},
				onError: (error) => {
					setToaster(true, ToastType.error, error?.message ?? 'Failed to upload visit images.', 5000);
				},
			}
		);
	}, [
		createVisit,
		formik.values,
		listProducts,
		uploadFreshSiteImages,
		refetchSiteSummaryList,
		setCurrentStep,
		setToaster,
		siteLeadData?.app_id_c,
	]);

	const handleNext = useCallback((): void => {
		const buildSiteLeadPayload = (values: typeof formik.values): UpdateSiteLeadData => ({
			site_type: !isKAESiteTypeFields ? values.siteType : '',
			dealer_code: values.dealerName.split('-')[0],
			dealer_name: values.dealerName.split('-')[1],
			glue_potential: values.gluePotentialOfSite,
			architect_name: values.architect_details[0].name,
			architect_mobile_no: values.architect_details[0].number,
			owner_name: values.owner_details[0].name,
			owner_mobile_no: values.owner_details[0].number,
			epoxy_adhesive_used_c: values.veneer,
			weather_work_given_to_joinery_c: values.isWeatherWorkGivenToJoinery,
			joinery_name: values.isWeatherWorkGivenToJoinery?.toLowerCase() === 'yes' ? values.joineryName : null,
			joinery_phone_number: values.isWeatherWorkGivenToJoinery?.toLowerCase() === 'yes' ? values.joineryNumber : null,
			joinery_address: values.isWeatherWorkGivenToJoinery?.toLowerCase() === 'yes' ? values.joineryAddress : null,
			weather_work_given_to_factory_c: values.isWorkGivenToFactory,
			factory_name: values.isWorkGivenToFactory?.toLowerCase() === 'yes' ? values.factoryName : null,
			factory_phone_number: values.isWorkGivenToFactory?.toLowerCase() === 'yes' ? values.factoryNumber : null,
			factory_address: values.isWorkGivenToFactory?.toLowerCase() === 'yes' ? values.factoryAddress : null,
			...(siteLeadData?.end_user_c !== null && siteLeadData?.end_user_c !== undefined
				? { end_user_c: siteLeadData?.end_user_c }
				: { lead_app_id_c: siteLeadData?.lead_app_id_c }),
		});

		const payload = buildSiteLeadPayload(formik.values);
		updateSiteLead(payload, {
			onSuccess: () => {
				updateSiteProducts();
			},
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [formik.values, updateSiteLead, updateSiteProducts, siteLeadData?.end_user_c]);

	const handleImageAdd = useCallback(
		(event: React.ChangeEvent<HTMLInputElement>) => {
			const maxImageLimit = 5;
			const folderName = 'sitelead';
			const maximumFileSize = 20 * 1024 * 1024;
			const result = handleAddImage(event, formik.values.productImages, maximumFileSize, maxImageLimit, folderName);
			if (result?.updatedImages) {
				formik.setFieldValue('productImages', result.updatedImages);
			}
		},
		[formik, handleAddImage]
	);

	const availableProductsList = useMemo((): DropdownOptions[] => {
		if (availableProductsDataList?.length) {
			return availableProductsDataList.map((product, index) => ({
				id: index,
				name: product?.productgroup3description_c,
				value: product?.prdgroup3_c ?? '',
			}));
		}
		return [];
	}, [availableProductsDataList]);

	const dealersList = useMemo((): DealersList[] => {
		if (dealerList?.length) {
			return dealerList.map((d, index) => ({
				id: index,
				value: `${d._data?.dealercode_c} -  ${d._data?.dealername_c}`,
				name: d._data?.dealername_c,
			}));
		}
		return [];
	}, [dealerList]);

	useEffect(() => {
		if (ownerDetails) {
			const details = [{ id: 0, isActive: true, name: ownerDetails.ownerName, number: ownerDetails.ownerNumber }];
			formik.setFieldValue('owner_details', details);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [ownerDetails]);

	const combinedSiteData = useMemo(() => {
		return {
			siteLeadData,
			dealerName,
		};
	}, [siteLeadData, dealerName]);

	useEffect(() => {
		formik.resetForm();

		if (combinedSiteData.siteLeadData?.current_stage_of_site_c?.toLowerCase() === 'site visited') {
			const {
				glue_potential,
				dealer_name,
				dealer_code,
				owner_name,
				owner_mobile_no,
				architect_name,
				architect_mobile_no,
				epoxy_adhesive_used_c,
				weather_work_given_to_joinery_c,
				joinery_name,
				joinery_phone_number,
				joinery_address,
				weather_work_given_to_factory_c,
				factory_name,
				factory_phone_number,
				factory_address,
			} = combinedSiteData.siteLeadData;

			formik.setFieldValue('gluePotentialOfSite', glue_potential);
			formik.setFieldValue('veneer', epoxy_adhesive_used_c);
			formik.setFieldValue('dealerName', `${dealer_code} - ${dealer_name}`);
			formik.setFieldValue('owner_details', [{ id: 0, isActive: true, name: owner_name, number: owner_mobile_no }]);
			formik.setFieldValue('architect_details', [
				{ id: 0, isActive: true, name: architect_name, number: architect_mobile_no },
			]);
			formik.setFieldValue('isWeatherWorkGivenToJoinery', weather_work_given_to_joinery_c ?? '');
			formik.setFieldValue('joineryName', joinery_name ?? '');
			formik.setFieldValue('joineryNumber', joinery_phone_number ?? '');
			formik.setFieldValue('joineryAddress', joinery_address ?? '');
			formik.setFieldValue('isWorkGivenToFactory', weather_work_given_to_factory_c ?? '');
			formik.setFieldValue('factoryName', factory_name ?? '');
			formik.setFieldValue('factoryNumber', factory_phone_number ?? '');
			formik.setFieldValue('factoryAddress', factory_address ?? '');
		}

		if (combinedSiteData.dealerName) {
			formik.setFieldValue('dealerName', combinedSiteData.dealerName);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [combinedSiteData]);

	const handleModalClose = useCallback(() => {
		setOpenQrModal(false);
		setConfirmationModal(true);
	}, []);

	const handleConfirmationModalClose = useCallback(() => {
		setOpenQrModal(true);
		setConfirmationModal(false);
	}, []);

	const handleConfirmSubmit = useCallback(() => {
		handleConfirmationModalClose();
		overlayRemoveFadeAction(() => setOpenQrModal(false));
		setCurrentStep(3);
	}, [handleConfirmationModalClose, setCurrentStep]);

	const shouldShowLoader = (...flags: Array<boolean | undefined>): boolean => flags.some(Boolean);
	const getFirstError = (...errors: Array<Error | null | undefined>): Error | null => {
		const found = errors.find((error): error is Error => error != null);
		return found ?? null;
	};

	const handleProductComplaintLast1MonthChange = (e: DropdownOptions): void => {
		if (e.value === 'No') {
			formik.setFieldValue('complaintProductsList', []);
			formik.setFieldValue('productComplaintDescription', []);
		}
	};

	const availableComplaintProductsList = useMemo((): DropdownOptions[] => {
		if (availableProductsDataList?.length) {
			return availableProductsDataList.map((product, index) => ({
				id: index,
				name: product?.productgroup3description_c,
				value: product?.productgroup3description_c ?? '',
			}));
		}
		return [];
	}, [availableProductsDataList]);

	const handleProductComplaintDescriptionChange = (e: DropdownOptions): void => {
		const hasOtherSelected = formik.values.productComplaintDescription.some((v) => v.value === OTHERS_SPECIFY);
		const isOtherSelected = e.value === OTHERS_SPECIFY;

		setMaxProductComplaintLimit(isOtherSelected && !hasOtherSelected ? 1 : 2);
		if (isOtherSelected) {
			formik.setFieldValue('customProductComplaintDescription', '');
			formik.setFieldValue(
				'productComplaintDescription',
				hasOtherSelected ? [] : [{ id: productComplaintDescriptionOptions.length, value: OTHERS_SPECIFY }]
			);
		}
	};

	return (
		<section className="new-site-section add-site-visit-wrapper">
			<div className="container">
				<Wrapper
					isLoaderVisible={shouldShowLoader(isTaskListProductsFetching, isSiteLeadFetching)}
					isError={getFirstError(taskListProductsError)}
				>
					<form>
						<ul className="new-site-wrapper row">
							{!isKAESiteTypeFields && (
								<li className="d-col d-col-2  form-radio">
									<RadioButton
										name="siteType"
										title="Site Type"
										checked={formik.values.siteType}
										radioOptions={siteRadioTypeList}
										onChange={formik.handleChange}
										isDisabled={isDisabled}
										required
									/>
								</li>
							)}
							<li className="d-col d-col-2 ">
								<Dropdown
									id="dealerName"
									name="dealerName"
									label="Dealer Name"
									onBlur={formik.handleBlur}
									title={formik.values.dealerName ?? ''}
									error={formik.touched.dealerName && formik.errors.dealerName ? formik.errors.dealerName : null}
									options={dealersList}
									setFieldValue={formik.setFieldValue}
									disabled={
										isDisabled &&
										!!(
											siteLeadData?.current_stage_of_site_c?.toLocaleLowerCase() === 'site visited' &&
											siteLeadData?.dealer_code &&
											siteLeadData?.dealer_name
										)
									}
									handelKeyDownProp={onDropDownSearch}
									listFetching={true}
									required
								/>
							</li>
							<li className="form-control d-col d-col-2">
								<TextInput
									id="gluePotentialOfSite"
									name="gluePotentialOfSite"
									onKeyDown={keyDownEvent}
									onBlur={formik.handleBlur}
									value={formik.values.gluePotentialOfSite ?? ''}
									setFieldValue={formik.setFieldValue}
									label="Glue Potential of Site (KG)"
									error={
										formik.touched.gluePotentialOfSite && formik.errors.gluePotentialOfSite
											? formik.errors.gluePotentialOfSite
											: null
									}
									disabled={
										isDisabled &&
										!!(
											siteLeadData?.current_stage_of_site_c?.toLocaleLowerCase() === 'site visited' &&
											siteLeadData?.glue_potential
										)
									}
									required
								/>
							</li>

							<li className="d-col">
								<span className="divider-border"></span>
							</li>

							{formik.values.owner_details.map((owner, index) => (
								<AccordianDetailList
									key={owner.id}
									title="Owner Details"
									accordianName="owner_details"
									accordianListIndex={index}
									formik={formik}
									data={owner}
									isEditing={
										isDisabled &&
										!!(
											siteLeadData?.current_stage_of_site_c?.toLocaleLowerCase() === 'site visited' &&
											siteLeadData?.owner_name &&
											siteLeadData?.owner_mobile_no
										)
									}
								/>
							))}
							{formik.values.architect_details.map((architect, index) => (
								<AccordianDetailList
									key={architect.id}
									title="Architect Details"
									accordianName="architect_details"
									accordianListIndex={index}
									formik={formik}
									data={architect}
									isEditing={isDisabled && !!(siteLeadData?.architect_name && siteLeadData?.architect_mobile_no)}
								/>
							))}

							<li className="d-col">
								<span className="divider-border"></span>
							</li>

							<li className="d-col d-col-2 ">
								<Dropdown
									id="isWeatherWorkGivenToJoinery"
									name="isWeatherWorkGivenToJoinery"
									label="Whether work is given to a Joinery?"
									onBlur={formik.handleBlur}
									title={formik.values.isWeatherWorkGivenToJoinery ?? ''}
									error={
										formik.touched.isWeatherWorkGivenToJoinery && formik.errors.isWeatherWorkGivenToJoinery
											? formik.errors.isWeatherWorkGivenToJoinery
											: null
									}
									options={veneerOptions}
									setFieldValue={formik.setFieldValue}
									required
								/>
							</li>

							{formik.values.isWeatherWorkGivenToJoinery?.toLowerCase() === 'yes' && (
								<>
									<li className="form-control d-col d-col-2">
										<TextInput
											id="joineryName"
											name="joineryName"
											type="text"
											onBlur={formik.handleBlur}
											value={formik.values.joineryName ?? ''}
											setFieldValue={formik.setFieldValue}
											label="Joinery Name"
											error={formik.touched.joineryName && formik.errors.joineryName ? formik.errors.joineryName : null}
											required
										/>
									</li>
									<li className="form-control d-col d-col-2">
										<TextInput
											id="joineryNumber"
											name="joineryNumber"
											type="text"
											onBlur={formik.handleBlur}
											value={formik.values.joineryNumber ?? ''}
											setFieldValue={formik.setFieldValue}
											onKeyDown={keyDownEvent}
											maxLength={10}
											label="Joinery Number"
											error={
												formik.touched.joineryNumber && formik.errors.joineryNumber ? formik.errors.joineryNumber : null
											}
											required
										/>
									</li>
									<li className="form-control d-col d-col-2">
										<TextInput
											id="joineryAddress"
											name="joineryAddress"
											type="text"
											onBlur={formik.handleBlur}
											value={formik.values.joineryAddress ?? ''}
											setFieldValue={formik.setFieldValue}
											label="Joinery Address"
											error={
												formik.touched.joineryAddress && formik.errors.joineryAddress
													? formik.errors.joineryAddress
													: null
											}
											required
										/>
									</li>
								</>
							)}

							<li className="d-col d-col-2 ">
								<Dropdown
									id="isWorkGivenToFactory"
									name="isWorkGivenToFactory"
									label="Whether work is given to a Factory?"
									onBlur={formik.handleBlur}
									title={formik.values.isWorkGivenToFactory ?? ''}
									error={
										formik.touched.isWorkGivenToFactory && formik.errors.isWorkGivenToFactory
											? formik.errors.isWorkGivenToFactory
											: null
									}
									options={veneerOptions}
									setFieldValue={formik.setFieldValue}
									required
								/>
							</li>

							{formik.values.isWorkGivenToFactory?.toLowerCase() === 'yes' && (
								<>
									<li className="form-control d-col d-col-2">
										<TextInput
											id="factoryName"
											name="factoryName"
											type="text"
											onBlur={formik.handleBlur}
											value={formik.values.factoryName ?? ''}
											setFieldValue={formik.setFieldValue}
											label="Factory Name"
											error={formik.touched.factoryName && formik.errors.factoryName ? formik.errors.factoryName : null}
											required
										/>
									</li>
									<li className="form-control d-col d-col-2">
										<TextInput
											id="factoryNumber"
											name="factoryNumber"
											type="text"
											onBlur={formik.handleBlur}
											value={formik.values.factoryNumber ?? ''}
											setFieldValue={formik.setFieldValue}
											onKeyDown={keyDownEvent}
											maxLength={10}
											label="Factory Number"
											error={
												formik.touched.factoryNumber && formik.errors.factoryNumber ? formik.errors.factoryNumber : null
											}
											required
										/>
									</li>
									<li className="form-control d-col d-col-2">
										<TextInput
											id="factoryAddress"
											name="factoryAddress"
											type="text"
											onBlur={formik.handleBlur}
											value={formik.values.factoryAddress ?? ''}
											setFieldValue={formik.setFieldValue}
											label="Factory Address"
											error={
												formik.touched.factoryAddress && formik.errors.factoryAddress
													? formik.errors.factoryAddress
													: null
											}
											required
										/>
									</li>
								</>
							)}

							<li className="d-col">
								<span className="divider-border"></span>
							</li>
							{listProducts &&
								listProducts.length > 0 &&
								listProducts.map((product) => {
									const isDiscussed = product?.product_discussed;
									const fieldName = isDiscussed ? 'discussedProducts' : 'demoedProducts';
									const isTouched = formik.touched[fieldName];
									const error = formik.errors[fieldName];

									return (
										<li className="d-col d-col-2" key={`${product.product_name}-${product.id}`}>
											<Dropdown
												id={fieldName}
												name={fieldName}
												label={`Product ${isDiscussed ? 'Discussion' : 'Demo'} ${product.product_name}`}
												onBlur={formik.handleBlur}
												title={
													fieldName === 'discussedProducts'
														? formik.values.discussedProducts ?? ''
														: formik.values.demoedProducts ?? ''
												}
												error={isTouched && error ? error : null}
												options={veneerOptions}
												setFieldValue={(_, value) => formik.setFieldValue(fieldName, value)}
												required
											/>
										</li>
									);
								})}

							<li className="d-col d-col-2">
								<ProductImages fieldName="productImages" formik={formik} handleAddImage={handleImageAdd} required />
							</li>
							<li className="d-col d-col-2">
								<MultiselectDropdown
									id="availableProducts"
									name="availableProducts"
									label="Product Available at Site"
									className="input-border"
									onBlur={formik.handleBlur}
									values={formik.values.availableProducts}
									options={availableProductsList}
									setFieldValue={formik.setFieldValue}
									maxLimit={3}
									error={
										formik.touched.availableProducts && formik.errors.availableProducts
											? formik.errors.availableProducts.toString()
											: null
									}
									required
								/>
							</li>
							{SiteLeadConfigurations.OTP_FLOW && (
								<li className="d-col d-col-2 ">
									<Dropdown
										id="isContractorAvailable"
										name="isContractorAvailable"
										label="Is contractor Present"
										onBlur={formik.handleBlur}
										title={formik.values.isContractorAvailable ?? ''}
										error={
											formik.touched.isContractorAvailable && formik.errors.isContractorAvailable
												? formik.errors.isContractorAvailable
												: null
										}
										options={veneerOptions}
										setFieldValue={formik.setFieldValue}
										required
									/>
								</li>
							)}
							<li className="d-col d-col-2 ">
								<Dropdown
									id="productComplaintLast1Month"
									name="productComplaintLast1Month"
									label="Have you faced any product concern in last one month ?"
									onBlur={formik.handleBlur}
									title={formik.values.productComplaintLast1Month ?? ''}
									error={
										formik.touched.productComplaintLast1Month && formik.errors.productComplaintLast1Month
											? formik.errors.productComplaintLast1Month
											: null
									}
									onChange={handleProductComplaintLast1MonthChange}
									options={productComplaintOptions}
									setFieldValue={formik.setFieldValue}
									required
								/>
							</li>

							{formik.values.productComplaintLast1Month === 'Yes' && (
								<li className="d-col d-col-2 ">
									<MultiselectDropdown
										id="complaintProductsList"
										name="complaintProductsList"
										label="Product Name"
										className="input-border"
										onBlur={formik.handleBlur}
										values={formik.values.complaintProductsList}
										options={availableComplaintProductsList}
										setFieldValue={formik.setFieldValue}
										maxLimit={3}
										required
										error={
											formik.touched.complaintProductsList && formik.errors.complaintProductsList
												? formik.errors.complaintProductsList.toString()
												: null
										}
									/>
								</li>
							)}
							{formik.values.productComplaintLast1Month === 'Yes' && (
								<li className="d-col d-col-2 ">
									<MultiselectDropdown
										id="productComplaintDescription"
										name="productComplaintDescription"
										label="Defect Name / Problem Description"
										className="input-border"
										onBlur={formik.handleBlur}
										values={formik.values.productComplaintDescription}
										options={productComplaintDescriptionOptions}
										onChange={handleProductComplaintDescriptionChange}
										setFieldValue={formik.setFieldValue}
										maxLimit={maxProductComplaintLimit}
										required
										error={
											formik.touched.productComplaintDescription && formik.errors.productComplaintDescription
												? formik.errors.productComplaintDescription.toString()
												: null
										}
									/>
								</li>
							)}

							{formik?.values?.productComplaintDescription?.[0]?.value === OTHERS_SPECIFY && (
								<li className="form-control d-col d-col-2">
									<TextInput
										id="customProductComplaintDescription"
										name="customProductComplaintDescription"
										maxLength={200}
										onBlur={formik.handleBlur}
										type="text"
										value={formik.values.customProductComplaintDescription ?? ''}
										setFieldValue={formik.setFieldValue}
										label="Other Defect Name / Problem Description"
										error={
											formik.touched.customProductComplaintDescription &&
											formik.errors.customProductComplaintDescription
												? formik.errors.customProductComplaintDescription
												: null
										}
										required
									/>
								</li>
							)}

							{activeLink && (
								<>
									<li className="d-col d-col-2">
										<div className="feedback-common-wrapper">
											<span className="h4 refrence-heading">
												Reference Forms
												{activeLink?.mandatory && <span className="bps-required-star">*</span>}
											</span>
											<div className="feedback-content">
												<p>
													Click link to fill form{' '}
													<a
														aria-label="feedback-form"
														href={activeLink.url}
														target="_blank"
														rel="noreferrer"
														onClick={(): void => {
															formik.setFieldValue('hasOpenedActiveLink', true, true);
														}}
													>
														- {activeLink.display_value}
													</a>
												</p>
											</div>
										</div>
									</li>
								</>
							)}
						</ul>
					</form>
				</Wrapper>

				<Stepper
					label="Submit"
					nextHandler={handleNext}
					isNext={
						!formik.isValid ||
						!formik.dirty ||
						isPutSiteImagePending ||
						!formik.values.productImages.length ||
						isSiteVisitsPending
					}
					isPending={isSiteLeadPending || isPutSiteImagePending || isSiteVisitsPending}
					totalSteps={2}
				/>
			</div>
			{openQrModal && (
				<Modal
					modalId="siteQrCode"
					className="dialog-sm site-lead-qr-code dialog-bottom"
					modalOpen={openQrModal}
					isEscAllow={false}
					onModalClose={handleModalClose}
					modalTitle={'Scan QR Code To Verify Site'}
				>
					<QrModal qrId={siteVisitId?.toString()} modalClose={handleModalClose} isCloseVisible={false} />
				</Modal>
			)}
			{openOtpModal && (
				<Modal
					modalId="siteOtpCode"
					className="dialog-sm site-lead-otp-code-modal dialog-bottom"
					modalOpen={openOtpModal}
					isEscAllow={false}
					onModalClose={handleModalClose}
					modalTitle={'Validate OTP'}
				>
					<OtpModal
						onClose={() => setConfirmationModal(true)}
						submitButtonLabel={'Generate OTP'}
						hideCancelButton={true}
						onSubmit={() => {
							setOpenOtpModal(false);
							handleConfirmSubmit();
						}}
					/>
				</Modal>
			)}
			{confirmationModal && (
				<Modal
					modalId="confirmationModal"
					className="dialog-sm site-lead-confrim"
					modalOpen={confirmationModal}
					isEscAllow={false}
					onModalClose={handleConfirmationModalClose}
				>
					<ConfirmationModal
						onClose={handleConfirmationModalClose}
						messageClassName={'h3'}
						onSubmit={handleConfirmSubmit}
						message="Are you sure you do not want to verify this site?"
						buttonLabel={'Confirm'}
					/>
				</Modal>
			)}
		</section>
	);
};
